import { appendGlobalExtData, useCookie } from '#imports'
import { authByLocalStorage, computeDurationDays } from '@/utils'
import { defineStore } from 'pinia'
import {
  EVipRegionType,
  getStorageUsage,
  getUserInfo,
  getVipPrivilege,
  type IpBlockRes,
  type IVipInfo,
  presetFreeDays,
} from '~/api'
import { StorageKey } from '~/types/enum'

interface UserInfoState {
  username?: string
  email?: string
  phone?: string
  premium?: boolean
  expire?: string
  status?: string
  type?: string
  userId?: string | null
  avatar?: string
  limit: number
  usage: number
  haveSetPassword: boolean // 是否已经设置了密码
  overFreeTaskLimit: number // 如果用户触发免费上传次数的限制，这里会有上限值。默认是 0，表示不限制
  overStorageLimit: boolean // 超出存储空间
  freeDays: number // 新用户赠送的免费天数，如果有，需要显示 PremiumPopover.vue 弹窗
  ipBlock?: IpBlockRes
  getFreeVipCalled: boolean // 是否请求过或者免费会员接口
  unsubscribeInfo: {
    email: string
    scene: string
    d: string
  }
  vipInfo: IVipInfo // 用户会员信息
}

interface UserInfoActions {
  setStorageUsage(): Promise<void>

  refreshStorageStatus(): Promise<void>

  refresh(enforce?: boolean): Promise<boolean>

  getVipDurationDays(vipType?: EVipRegionType): number
}

export const useUserInfo = defineStore<'userInfo', UserInfoState, Record<string, never>, UserInfoActions>('userInfo', {
  state: () => ({
    username: undefined,
    email: undefined,
    phone: undefined,
    premium: undefined,
    expire: undefined,
    status: undefined,
    type: undefined,
    userId: import.meta.server ? useCookie(StorageKey.USER_ID).value : undefined,
    avatar: undefined,
    limit: 0,
    usage: 0,
    haveSetPassword: false,
    overFreeTaskLimit: 0,
    overStorageLimit: false,
    freeDays: 0,
    ipBlock: undefined,
    getFreeVipCalled: false,
    vipInfo: {} as IVipInfo,
    unsubscribeInfo: {
      email: '',
      scene: '',
      d: '',
    },
  }),
  actions: {
    async setStorageUsage() {
      const { data, error } = await getStorageUsage()
      if (data.value) {
        const {
          quota: { limit, usage },
        } = data.value
        this.$patch({
          limit: Number(limit),
          usage: Number(usage),
        })
      } else {
        console.error(error.value.message)
      }
    },
    async refreshStorageStatus() {
      const [{ data: vipInfoData, error: vipInfoError }] = await Promise.all([
        getVipPrivilege(),
        this.setStorageUsage(),
      ])

      if (vipInfoError.value !== null) return

      const {
 expire, status, type, user_id: userId,
} = vipInfoData.value?.data || {}

      this.$subscribe(async (_, state) => {
        // 有 userId，不是会员，并且当前没有被赠送
        if (!state.userId || state.premium || state.freeDays || state.getFreeVipCalled) return
        /* 不是会员，尝试赠送会员，并且在 this.$patch 之后执行，确保 userId 有值 */
        const freeDays = (await presetFreeDays()).data.value?.free_days || 0
        this.freeDays = freeDays
        if (freeDays) {
          useCookie(StorageKey.FREE_DAYS, { maxAge: 24 * 60 * 60 * freeDays }).value = freeDays.toString()
          this.$patch({ premium: true }) // 修改会员状态
          window.gtag('event', 'sign_up') // 如果赠送成功，说明是新注册的用户, GA  埋点
        }
      })

      appendGlobalExtData({ userType: status === 'ok' ? 2 : 1 })

      this.$patch({
        expire,
        status,
        type,
        userId,
        premium: status === 'ok',
        vipInfo: vipInfoData.value?.data || {},
      })
    },
    async refresh(enforce = false) {
      const clearAvatarCookie = () => {
        this.$patch({ userId: null })
        useCookie(StorageKey.USER_ID).value = null
      }
      if (!enforce && !authByLocalStorage()) {
        clearAvatarCookie()
        return false
      }
      try {
        const [{ data, error }] = await Promise.all([getUserInfo(), this.refreshStorageStatus()])
        if (error.value !== null) {
          /* 在其它 TAB 退出登录态后，及时清除当前 TAB 的登录态 */
          clearAvatarCookie()
          return false
        }
        const {
 email, name: username, password, phone_number: phone, picture: avatar, sub,
} = data.value

        this.$patch({
          avatar,
          email,
          haveSetPassword: /^set$/i.test(password),
          phone,
          username,
        })

        useCookie(StorageKey.USER_ID, {
          path: '/',
          maxAge: 365 * 24 * 60 * 60,
          sameSite: 'lax',
          secure: true,
        }).value = sub
        return true
      } catch (e) {
        clearAvatarCookie()
        return false
      }
    },

    getVipDurationDays(vipType?: EVipRegionType) {
      let expireTime = new Date(this.expire || this.vipInfo.expire || 0).getTime()
      if (vipType && this.vipInfo.vipItem) {
        const item = this.vipInfo.vipItem.find(i => i.type === vipType)
        if (item?.surplus_day) return item?.surplus_day
        expireTime = item?.expire ? new Date(item.expire).getTime() : Date.now()
      }
      const days = computeDurationDays(Date.now(), expireTime)
      return days < 0 ? 0 : days
    },
  },
})
